import React from 'react';
import './ImageRequirements.css';

function ImageRequirements() {
  return (
    <div className="image-requirements container">
      <h1>📸 Product Image Requirements</h1>
      <p className="intro">To ensure your products look professional and attract customers, please follow these image guidelines.</p>

      <div className="requirement-section">
        <h2>✅ Minimum Requirements</h2>
        <div className="requirement-grid">
          <div className="requirement-card">
            <h3>Resolution</h3>
            <p><strong>Minimum:</strong> 1200 x 1200 pixels</p>
            <p><strong>Recommended:</strong> 2000 x 2000 pixels or higher</p>
            <p>Higher resolution ensures images look sharp on all devices and can be zoomed.</p>
          </div>

          <div className="requirement-card">
            <h3>File Format</h3>
            <p><strong>Accepted:</strong> JPEG, JPG, PNG, GIF, WebP</p>
            <p><strong>Best for photos:</strong> JPEG</p>
            <p><strong>Best for transparency:</strong> PNG</p>
          </div>

          <div className="requirement-card">
            <h3>File Size</h3>
            <p><strong>Maximum:</strong> 10MB per image</p>
            <p><strong>Recommended:</strong> 500KB - 3MB</p>
            <p>Optimize images for faster loading while maintaining quality.</p>
          </div>

          <div className="requirement-card">
            <h3>Aspect Ratio</h3>
            <p><strong>Preferred:</strong> Square (1:1)</p>
            <p><strong>Alternative:</strong> 4:3 or 3:4</p>
            <p>Square images display consistently across the site.</p>
          </div>
        </div>
      </div>

      <div className="requirement-section">
        <h2>📷 Photography Guidelines</h2>
        
        <div className="guideline-section">
          <h3>Lighting</h3>
          <ul className="checklist">
            <li className="good">✅ Use natural daylight or professional lighting</li>
            <li className="good">✅ Ensure even lighting with no harsh shadows</li>
            <li className="good">✅ Keep lighting consistent across all photos</li>
            <li className="bad">❌ Avoid yellow indoor lighting or camera flash</li>
          </ul>
        </div>

        <div className="guideline-section">
          <h3>Background</h3>
          <ul className="checklist">
            <li className="good">✅ Use clean white or neutral background</li>
            <li className="good">✅ Keep background consistent for all products</li>
            <li className="good">✅ Remove distracting elements from frame</li>
            <li className="bad">❌ Avoid cluttered or busy backgrounds</li>
          </ul>
        </div>

        <div className="guideline-section">
          <h3>Product Presentation</h3>
          <ul className="checklist">
            <li className="good">✅ Product should fill 80-90% of frame</li>
            <li className="good">✅ Include multiple angles (front, back, details)</li>
            <li className="good">✅ Show texture and details clearly</li>
            <li className="good">✅ Include scale reference if size matters</li>
            <li className="bad">❌ No watermarks or text overlays</li>
            <li className="bad">❌ Product should not be cut off</li>
          </ul>
        </div>

        <div className="guideline-section">
          <h3>Technical Quality</h3>
          <ul className="checklist">
            <li className="good">✅ Sharp focus on the product</li>
            <li className="good">✅ True-to-life, accurate colors</li>
            <li className="good">✅ Good contrast and proper exposure</li>
            <li className="good">✅ Correct orientation (not sideways)</li>
            <li className="bad">❌ No blur, pixelation, or distortion</li>
          </ul>
        </div>
      </div>

      <div className="requirement-section">
        <h2>📋 Pre-Upload Checklist</h2>
        <div className="checklist-box">
          <p>Before uploading your product images, verify:</p>
          <ul>
            <li>☐ Image is at least 1200 x 1200 pixels</li>
            <li>☐ File size is under 10MB</li>
            <li>☐ Photo is in focus and well-lit</li>
            <li>☐ Background is clean and consistent</li>
            <li>☐ Colors look accurate</li>
            <li>☐ No blur, pixelation, or distortion</li>
            <li>☐ Proper orientation (not rotated incorrectly)</li>
            <li>☐ No watermarks or logos</li>
          </ul>
        </div>
      </div>

      <div className="requirement-section">
        <h2>🛠️ Recommended Tools</h2>
        <div className="tools-grid">
          <div className="tool-card">
            <h4>Image Compression</h4>
            <ul>
              <li><a href="https://tinypng.com" target="_blank" rel="noopener noreferrer">TinyPNG.com</a> - Compress without quality loss</li>
              <li><a href="https://squoosh.app" target="_blank" rel="noopener noreferrer">Squoosh.app</a> - Advanced compression</li>
              <li><a href="https://compressor.io" target="_blank" rel="noopener noreferrer">Compressor.io</a> - Bulk compression</li>
            </ul>
          </div>
          <div className="tool-card">
            <h4>Editing & Background Removal</h4>
            <ul>
              <li><a href="https://www.canva.com" target="_blank" rel="noopener noreferrer">Canva.com</a> - Easy background removal</li>
              <li><a href="https://www.remove.bg" target="_blank" rel="noopener noreferrer">Remove.bg</a> - Automatic background removal</li>
              <li><a href="https://www.photopea.com" target="_blank" rel="noopener noreferrer">Photopea.com</a> - Free online editor</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="requirement-section warning-section">
        <h2>⚠️ Common Issues to Avoid</h2>
        <div className="warning-grid">
          <div className="warning-item">
            <strong>❌ Too Small:</strong> Images under 1200x1200px look pixelated when zoomed
          </div>
          <div className="warning-item">
            <strong>❌ Too Large:</strong> Files over 10MB won't upload
          </div>
          <div className="warning-item">
            <strong>❌ Poor Lighting:</strong> Dark photos look unprofessional
          </div>
          <div className="warning-item">
            <strong>❌ Blurry:</strong> Out of focus reduces customer trust
          </div>
          <div className="warning-item">
            <strong>❌ Wrong Colors:</strong> Inaccurate colors mislead customers
          </div>
          <div className="warning-item">
            <strong>❌ Busy Background:</strong> Distracts from the product
          </div>
        </div>
      </div>

      <div className="requirement-section example-section">
        <h2>✨ Example of Good Product Photo</h2>
        <div className="example-box">
          <ul>
            <li><strong>Product:</strong> Traditional cultural attire</li>
            <li><strong>Resolution:</strong> 2000 x 2000 pixels</li>
            <li><strong>Background:</strong> Pure white or neutral</li>
            <li><strong>Lighting:</strong> Soft, even lighting</li>
            <li><strong>Angles:</strong> Front view, back view, detail shots</li>
            <li><strong>File Size:</strong> ~1-2MB JPEG at 90% quality</li>
          </ul>
        </div>
      </div>

      <div className="requirement-section note-section">
        <h2>📝 Important Note</h2>
        <p>While you can submit images that don't meet these requirements, we strongly recommend following these guidelines. Low-quality images may result in:</p>
        <ul>
          <li>Lower customer confidence</li>
          <li>Reduced sales</li>
          <li>Request to resubmit with better images</li>
        </ul>
        <p><strong>Professional images = Professional business = More sales!</strong></p>
      </div>
    </div>
  );
}

export default ImageRequirements;
